import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

/**
 * Menu déroulant de filtre/tri contrôlé (présentationnel). Le panneau est rendu
 * via un portal en position `fixed` → il échappe aux stacking contexts (parents
 * avec backdrop-filter / transform / opacity) et ne passe jamais sous les autres
 * éléments, comme le faisait un <select> natif. Options : `[{ value, label }]`.
 */
const PANEL_WIDTH = 192; // w-48

const SelectFiltre = ({ label, value, options, onChange, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const toggle = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      // Clamp horizontal pour ne pas déborder du viewport (le natif gérait ça seul).
      const left = Math.max(8, Math.min(r.left, window.innerWidth - PANEL_WIDTH - 8));
      setCoords({ top: r.bottom + 8, left });
    }
    setIsOpen((v) => !v);
  };

  // Comme un <select> natif : refermer au scroll / resize / Échap.
  useEffect(() => {
    if (!isOpen) return undefined;
    const close = () => setIsOpen(false);
    const onKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-2 sm:py-2.5 bg-white border border-slate-200 rounded-lg sm:rounded-xl hover:border-slate-300 transition-colors text-xs sm:text-sm font-medium text-slate-700"
      >
        {Icon && <Icon size={14} className="text-slate-500 hidden sm:block" />}
        <span className="text-slate-500 hidden sm:inline">{label}:</span>
        <span className="truncate max-w-[60px] sm:max-w-none">{value}</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && createPortal(
        <>
          <div className="fixed inset-0 z-[1000]" onClick={() => setIsOpen(false)} />
          <div
            role="listbox"
            className="fixed z-[1001] w-40 sm:w-48 bg-white border border-slate-200 rounded-xl shadow-xl py-2"
            style={{ top: coords.top, left: coords.left }}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={value === option.label}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 sm:px-4 py-2 text-xs sm:text-sm transition-colors ${
                  value === option.label ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
};

export default SelectFiltre;
